import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Upload, ClipboardCheck, Smartphone, ShieldCheck, RefreshCw, Sun, MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Wordmark } from "@/components/marketing/Wordmark";
import { BuiltOSMark } from "@/components/common/BuilderLogo";
import { HeroMockups } from "@/components/marketing/DeviceMockups";
import { XeroGapSection, UnfairAdvantagesGrid, SitePulseSection, PRIMARY_CTA } from "@/components/marketing/PitchSections";
import {
  BookingLoopSection,
  ClientExperienceSection,
  QualityComplianceSection,
  ProfitProtectionSection,
  FeatureCategoriesGrid,
  FinalValueBanner,
} from "@/components/marketing/SpectrumSections";
import { usePinnedColorTheme } from "@/hooks/useColorTheme";
import { useAuth } from "@/context/AuthContext";

const TAGLINE = "Built by Builders, for Builders. The end-to-end OS that gives you your time back.";

const STEPS = [
  { n: "01", icon: Upload, title: "Drop your plans", body: "PDF sets, sketches or a whole folder. Revisions are detected and filed automatically." },
  { n: "02", icon: ClipboardCheck, title: "Review BOQ & margin", body: "Check quantities, apply your rates, lock the margin and send a branded quote in one click." },
  { n: "03", icon: Smartphone, title: "Run the job from site", body: "Schedules, diaries, claims and rectifications — all from your phone, all in sync with Xero." },
];

const TRUST_PILLS = [
  { icon: RefreshCw, label: "Live Xero sync" },
  { icon: ShieldCheck, label: "GST & progress claims built in" },
  { icon: Sun, label: "Readable in full sun, on site" },
  { icon: MapPin, label: "Made in Melbourne by working builders" },
];

const Container = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-[1120px] px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

export default function LandingPage() {
  usePinnedColorTheme("builtos"); // the public brand experience is always BuiltOS Blue
  const { user } = useAuth();

  useEffect(() => {
    const prev = document.title;
    document.title = "BuiltOS — Built by Builders, for Builders";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* ---------------------------------------------------------------- nav */}
      <header className="sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-md" data-testid="landing-nav">
        <Container className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="BuiltOS home" data-testid="landing-nav-wordmark">
            <BuiltOSMark className="h-8 w-8" />
            <Wordmark as="span" />
          </Link>
          <nav className="hidden items-center gap-6 text-sm font-medium text-muted-foreground md:flex" aria-label="Primary">
            <a href="#why-builtos" className="transition-colors hover:text-foreground" data-testid="landing-nav-why-link">
              Why BuiltOS
            </a>
            <a href="#advantages" className="transition-colors hover:text-foreground" data-testid="landing-nav-advantages-link">
              Advantages
            </a>
            <a href="#booking-loop" className="transition-colors hover:text-foreground" data-testid="landing-nav-booking-link">
              Booking
            </a>
            <a href="#client-experience" className="transition-colors hover:text-foreground" data-testid="landing-nav-clients-link">
              Clients
            </a>
            <a href="#features" className="transition-colors hover:text-foreground" data-testid="landing-nav-features-link">
              Product
            </a>
            <a href="#how-it-works" className="transition-colors hover:text-foreground" data-testid="landing-nav-how-link">
              How it works
            </a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Button asChild size="sm" className="h-9 rounded-[10px] px-4" data-testid="landing-nav-open-app-button">
                <Link to="/">Open BuiltOS</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" className="h-9 rounded-[10px] px-3 text-foreground" data-testid="landing-nav-sign-in-link">
                  <Link to="/auth">Sign in</Link>
                </Button>
                <Button asChild size="sm" className="h-9 rounded-[10px] px-4 shadow-[var(--shadow-card)]" data-testid="landing-nav-get-started-button">
                  <Link to="/signup">Get Started</Link>
                </Button>
              </>
            )}
          </div>
        </Container>
      </header>

      <main>
        {/* --------------------------------------------------------------- hero */}
        <section
          className="relative overflow-hidden pb-14 pt-14 sm:pb-20 sm:pt-20"
          style={{ backgroundImage: "radial-gradient(900px circle at 18% -10%, hsl(var(--primary) / 0.10), transparent 55%)" }}
          data-testid="landing-hero"
        >
          <Container>
            <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-10">
              <div className="lg:col-span-5">
                <p className="inline-flex items-center gap-2 rounded-full border border-[rgba(11,42,74,0.14)] bg-white px-3 py-1 text-xs font-medium text-primary shadow-[var(--shadow-card)]" data-testid="landing-hero-eyebrow">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--chart-net))]" aria-hidden="true" />
                  Construction management for Australian builders
                </p>
                <h1
                  className="mt-5 max-w-[18ch] font-heading text-4xl font-semibold leading-[1.05] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3rem] lg:leading-[1.06] xl:text-[3.25rem]"
                  data-testid="landing-hero-headline"
                >
                  {TAGLINE}
                </h1>
                <p className="mt-5 max-w-[56ch] text-base leading-7 text-muted-foreground md:text-lg" data-testid="landing-hero-subhead">
                  One workspace from the first sketch to the final claim: AI take-offs that price themselves, a
                  finance centre that guards your margin, and a site view your supervisors actually use.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                    asChild
                    size="lg"
                    className="h-11 rounded-[10px] px-6 text-[15px] shadow-[var(--shadow-card)] transition-[box-shadow,transform,background-color] duration-150 hover:-translate-y-px hover:shadow-[var(--shadow-lift)] active:translate-y-0 active:scale-[0.98]"
                    data-testid="landing-hero-get-started-button"
                  >
                    <Link to="/signup">
                      {PRIMARY_CTA} <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="secondary" size="lg" className="h-11 rounded-[10px] border border-border px-6 text-[15px]" data-testid="landing-hero-sign-in-link">
                    <Link to="/auth">Sign in</Link>
                  </Button>
                </div>
                <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground" data-testid="landing-hero-proof-points">
                  {["No credit card", "Workspace ready in under a business day", "Your rate book, your margins"].map((t) => (
                    <li key={t} className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--chart-received))]" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-7">
                <HeroMockups />
              </div>
            </div>
          </Container>
        </section>

        {/* ------------------------------------------------------- trust banner */}
        <section className="border-y border-border bg-[hsl(210_28%_96%)] py-8" data-testid="landing-trust-banner">
          <Container className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <p className="text-sm font-medium text-muted-foreground" data-testid="landing-trust-headline">
              Trusted by construction professionals across Australia.
            </p>
            <ul className="flex flex-wrap gap-2">
              {TRUST_PILLS.map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(11,42,74,0.14)] bg-white px-3 py-1 text-xs font-medium text-foreground"
                  data-testid={`landing-trust-pill-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <Icon className="h-3.5 w-3.5 text-primary" /> {label}
                </li>
              ))}
            </ul>
          </Container>
        </section>

        {/* ------------------------------------ "Ultimate Builder OS" pitch sections */}
        <XeroGapSection />
        <UnfairAdvantagesGrid />
        <BookingLoopSection />
        <SitePulseSection />

        {/* ------------------------------ "Full-Spectrum" Builder OS: clients · quality · profit */}
        <ClientExperienceSection />
        <QualityComplianceSection />
        <ProfitProtectionSection />

        {/* --------------------------- features grouped: Smart Estimating · Site Ops · Financials */}
        <FeatureCategoriesGrid />

        {/* ------------------------------------------------------- how it works */}
        <section id="how-it-works" className="py-16 sm:py-20" data-testid="landing-how-it-works">
          <Container>
            <div className="max-w-[60ch]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">How it works</p>
              <h2 className="mt-2 font-heading text-3xl font-semibold tracking-[-0.02em] sm:text-4xl">Plans in. Profitable job out.</h2>
            </div>
            <ol className="mt-10 grid gap-6 md:grid-cols-3">
              {STEPS.map(({ n, icon: Icon, title, body }) => (
                <li key={n} className="relative rounded-[16px] border border-border bg-card p-6 shadow-[var(--shadow-card)]" data-testid={`landing-step-${n}`}>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-primary/30 bg-accent font-mono-nums text-xs font-semibold text-primary">
                      {n}
                    </span>
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{body}</p>
                </li>
              ))}
            </ol>
          </Container>
        </section>
      </main>

      {/* --------------------------- final value statement (closing CTA banner) */}
      <FinalValueBanner cta={PRIMARY_CTA} />

      {/* --------------------------------------------------------------- footer */}
      <footer className="border-t border-border bg-[hsl(210_28%_96%)] py-10" data-testid="landing-footer">
        <Container className="grid gap-8 md:grid-cols-2">
          <div>
            <Wordmark to="/" size="sm" />
            <p className="mt-3 max-w-[40ch] text-sm leading-6 text-muted-foreground">
              The end-to-end operating system for Australian builders — quoting, site and money in one place.
            </p>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground md:justify-end" aria-label="Footer">
            <a href="#features" className="hover:text-foreground hover:underline" data-testid="footer-product-link">
              Product
            </a>
            <a href="#booking-loop" className="hover:text-foreground hover:underline" data-testid="footer-booking-link">
              Smart Booking Loop
            </a>
            <a href="#client-experience" className="hover:text-foreground hover:underline" data-testid="footer-clients-link">
              Client portal
            </a>
            <a href="#how-it-works" className="hover:text-foreground hover:underline" data-testid="footer-how-link">
              How it works
            </a>
            <Link to="/signup" className="hover:text-foreground hover:underline" data-testid="footer-get-started-link">
              Get started
            </Link>
            <Link to="/auth" className="hover:text-foreground hover:underline" data-testid="footer-sign-in-link">
              Sign in
            </Link>
          </nav>
          <p className="text-xs text-muted-foreground md:col-span-2" data-testid="footer-fineprint">
            © {new Date().getFullYear()} BuiltOS · Built by builders, for builders · Melbourne, Australia
          </p>
        </Container>
      </footer>
    </div>
  );
}
